import React, { useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Alert } from '../components/ui/alert';
import { Label } from '../components/ui/label';
import { Eye, EyeOff, UserPlus } from 'lucide-react';

const Register: React.FC = () => {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    name: '',
    surname: '',
    phone: '',
    fin: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [field]: e.target.value });
    setError(null);
    setSuccessMessage(null);
  };

  const validateForm = (): boolean => {
    if (!formData.email.trim()) {
      setError(t('emailRequired'));
      return false;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      setError(t('invalidEmailFormat'));
      return false;
    }
    if (!formData.password) {
      setError(t('passwordRequired'));
      return false;
    }
    if (formData.password.length < 6) {
      setError(t('passwordMinLength'));
      return false;
    }
    if (formData.password !== formData.confirmPassword) {
      setError(t('passwordsDoNotMatch'));
      return false;
    }
    if (!formData.name.trim()) {
      setError(t('nameRequired'));
      return false;
    }
    if (!formData.surname.trim()) {
      setError(t('surnameRequired'));
      return false;
    }
    const phoneNormalized = formData.phone.replace(/[\s()-]/g, '');
    if (!phoneNormalized) {
      setError(t('phoneRequired'));
      return false;
    }
    if (!/^\+?[0-9]{7,15}$/.test(phoneNormalized)) {
      setError(t('invalidPhoneFormat'));
      return false;
    }
    if (!formData.fin.trim()) {
      setError(t('finRequired'));
      return false;
    }
    if (!/^[A-Za-z0-9]{7}$/.test(formData.fin)) {
      setError(t('finCodeLength'));
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccessMessage(null);

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      const { message, loggedIn } = await register({
        email: formData.email,
        password: formData.password,
        name: formData.name,
        surname: formData.surname,
        phone: formData.phone.replace(/[\s()-]/g, ''),
        fin: formData.fin.trim(),
      });
      if (loggedIn) {
        navigate('/documents');
        return;
      }
      setSuccessMessage(message);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('registrationFailed'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="mx-auto w-full max-w-2xl">
      <Card>
        <CardHeader>
          <CardTitle className="text-center text-3xl">{t('signUp')}</CardTitle>
          <CardDescription className="text-center">{t('createAccount')}</CardDescription>
        </CardHeader>
        <CardContent>
          {successMessage ? (
            <div className="space-y-4">
              <Alert variant="success">{successMessage}</Alert>
              <Button className="w-full" asChild>
                <RouterLink to="/login">{t('signIn')}</RouterLink>
              </Button>
            </div>
          ) : (
            <>
          {error && (
            <Alert variant="destructive" className="mb-4 whitespace-pre-line">
              {error.includes('\n') ? (
                <ul className="list-disc space-y-1 pl-4">
                  {error.split('\n').map((line, idx) => (
                    <li key={idx}>{line}</li>
                  ))}
                </ul>
              ) : (
                error
              )}
            </Alert>
          )}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div><Label>{t('name')}</Label><Input required value={formData.name} onChange={handleChange('name')} /></div>
              <div><Label>{t('surname')}</Label><Input required value={formData.surname} onChange={handleChange('surname')} /></div>
            </div>
            <div><Label>{t('emailAddress')}</Label><Input required type="email" autoComplete="email" value={formData.email} onChange={handleChange('email')} /></div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <Label>{t('password')}</Label>
                <div className="relative">
                  <Input required type={showPassword ? "text" : "password"} value={formData.password} onChange={handleChange('password')} className="pr-10" />
                  <button type="button" className="absolute right-2 top-2 rounded p-1" onClick={() => setShowPassword(!showPassword)}>{showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</button>
                </div>
              </div>
              <div>
                <Label>{t('confirmPassword')}</Label>
                <div className="relative">
                  <Input required type={showConfirmPassword ? "text" : "password"} value={formData.confirmPassword} onChange={handleChange('confirmPassword')} className="pr-10" />
                  <button type="button" className="absolute right-2 top-2 rounded p-1" onClick={() => setShowConfirmPassword(!showConfirmPassword)}>{showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</button>
                </div>
              </div>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div><Label>{t('phoneNumber')}</Label><Input required type="tel" value={formData.phone} onChange={handleChange('phone')} placeholder="+994 12 345 67 89" /></div>
              <div>
                <Label>{t('finCode')}</Label>
                <Input required value={formData.fin} onChange={handleChange('fin')} maxLength={7} />
                <p className="mt-1 text-xs text-muted-foreground">{t('finCodeHelper')}</p>
              </div>
            </div>
            <Button type="submit" className="w-full" disabled={isLoading}><UserPlus className="h-4 w-4" />{isLoading ? t('creatingAccount') : t('signUp')}</Button>
            <p className="text-center text-sm text-muted-foreground">
              <RouterLink to="/login" className="text-primary hover:underline">{t('alreadyHaveAccount')} {t('signIn')}</RouterLink>
            </p>
          </form>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Register;

